import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { EvalRun, EvalRunStatus } from '@super/shared-types'

interface Props {
  runs: EvalRun[]
  total: number
  limit: number
  offset: number
  onOffsetChange: (offset: number) => void
  onSelectRun: (run: EvalRun) => void
  isLoading: boolean
}

function StatusBadge({ status }: { status: EvalRunStatus }) {
  const variants: Record<EvalRunStatus, { label: string; className: string }> =
    {
      PENDING: {
        label: 'Pending',
        className: 'bg-muted text-muted-foreground',
      },
      RUNNING: {
        label: 'Running',
        className: 'bg-info-muted text-info animate-pulse',
      },
      COMPLETED: {
        label: 'Completed',
        className: 'bg-success-muted text-success',
      },
      FAILED: { label: 'Failed', className: 'bg-error-muted text-error' },
    }
  const { label, className } = variants[status]
  return <Badge className={className}>{label}</Badge>
}

function passRate(run: EvalRun) {
  if (!run.totalCases) return '—'
  return `${Math.round((run.passedCases / run.totalCases) * 100)}%`
}

function formatCost(usd: number | null | undefined) {
  if (usd == null) return '—'
  return `$${usd.toFixed(4)}`
}

export function EvalRunsTable({
  runs,
  total,
  limit,
  offset,
  onOffsetChange,
  onSelectRun,
  isLoading,
}: Props) {
  const currentPage = Math.floor(offset / limit) + 1
  const totalPages = Math.ceil(total / limit)

  return (
    <div className="flex flex-col gap-3">
      <FloatingActionIslandContent
        show={totalPages > 1}
        componentId="eval-runs-pagination"
        priority={10}
      >
        <div className="inline-flex w-full lg:w-[520px] max-w-2xl">
          <div className="flex-grow items-center space-x-2 text-sm text-muted-foreground whitespace-nowrap pointer-events-none hidden md:flex">
            <span className="font-medium">
              {offset + 1}–{Math.min(offset + limit, total)} of {total} runs
            </span>
          </div>
          <div className="flex items-center space-x-2 w-[240px] justify-center md:justify-end pointer-events-auto">
            <div className="inline-flex items-center space-x-1">
              <button
                onClick={() => onOffsetChange(offset - limit)}
                disabled={currentPage <= 1}
                className="pointer-events-auto relative inline-flex items-center justify-center w-10 h-10 text-sm font-medium text-muted-foreground bg-background/80 border border-border rounded-lg hover:bg-muted hover:border-border disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="inline-flex items-center space-x-1 justify-end">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNumber
                  if (totalPages <= 5) {
                    pageNumber = i + 1
                  } else if (currentPage <= 3) {
                    pageNumber = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    pageNumber = totalPages - 4 + i
                  } else {
                    pageNumber = currentPage - 2 + i
                  }
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => onOffsetChange((pageNumber - 1) * limit)}
                      className={cn(
                        'pointer-events-auto relative inline-flex items-center justify-center w-10 h-10 text-sm font-medium border rounded-lg transition-all',
                        pageNumber === currentPage
                          ? 'z-10 bg-black/90 text-white border-black'
                          : 'bg-background/80 border-border text-muted-foreground hover:bg-muted hover:border-border',
                      )}
                    >
                      {pageNumber}
                    </button>
                  )
                })}
              </div>
              <button
                onClick={() => onOffsetChange(offset + limit)}
                disabled={currentPage >= totalPages}
                className="pointer-events-auto relative inline-flex items-center justify-center w-10 h-10 text-sm font-medium text-muted-foreground bg-background/80 border border-border rounded-lg hover:bg-muted hover:border-border disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </FloatingActionIslandContent>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Prompt type</TableHead>
              <TableHead>Version</TableHead>
              <TableHead>Model</TableHead>
              <TableHead>Cases</TableHead>
              <TableHead>Pass rate</TableHead>
              <TableHead>Est. cost</TableHead>
              <TableHead>Actual cost</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-28" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-12" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-8" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-12" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-20" />
                  </TableCell>
                </TableRow>
              ))
            ) : runs.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="text-center text-muted-foreground py-8"
                >
                  No eval runs yet.
                </TableCell>
              </TableRow>
            ) : (
              runs.map(run => (
                <TableRow
                  key={run.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => onSelectRun(run)}
                >
                  <TableCell className="text-sm whitespace-nowrap">
                    {new Date(run.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{run.promptType ?? '—'}</Badge>
                  </TableCell>
                  <TableCell className="text-xs font-mono text-muted-foreground">
                    {run.promptVersion ?? '—'}
                  </TableCell>
                  <TableCell className="text-xs font-mono text-muted-foreground">
                    {run.model}
                  </TableCell>
                  <TableCell>{run.totalCases}</TableCell>
                  <TableCell>{passRate(run)}</TableCell>
                  <TableCell>{formatCost(run.estimatedCostUsd)}</TableCell>
                  <TableCell>{formatCost(run.actualCostUsd)}</TableCell>
                  <TableCell>
                    <StatusBadge status={run.status as EvalRunStatus} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
